import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useOperator } from "@sinch/components/application/ConditionBuilder/useOperator";
import { useConditionCategories } from "@sinch/components/application/RateCondition/useConditionCategories";
import { useRateConditions } from "@sinch/components/application/RateCondition/useRateConditions";
import { useRateConditionPossibleValues } from "@sinch/components/application/RateCondition/useRateConditionPossibleValues";
import { useDeletedConditionSubjectLabel } from "@sinch/components/application/RateCondition/useDeletedConditionSubjectLabel";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { RateConditionSchemaFields } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { OverflowTooltip } from "@sinch/components/typography/OverflowTooltip/OverflowTooltip";

export const RateConditionOverview: FC<{ condition: RateConditionSchemaFields | null }> = ({ condition }) => {
  const { t } = useTranslation();
  const { rateType } = useRateBuilderSettings();
  const { getLabel } = useConditionCategories(rateType);
  const { getLabel: getConditionLabel, isSubjectMissing } = useRateConditions(condition?.category);
  const { getValueLabel } = useRateConditionPossibleValues(condition);
  const { getDeletedSubjectLabel } = useDeletedConditionSubjectLabel();
  const { getOperatorLabel } = useOperator();

  if (condition === null) {
    return t("RateBuilder.noConditionOption");
  }

  // a badge / attribute deleted after the ratelist was saved: name what is missing instead of an operator and a raw
  // value that no longer mean anything
  const subjectMissing = isSubjectMissing(condition.subjectId, condition.type);
  const valString = subjectMissing ? null : getValueLabel(condition.value);
  const subjectLabel = subjectMissing
    ? getDeletedSubjectLabel(condition.category)
    : getConditionLabel(condition.subjectId, condition.type);
  const categoryLabel = condition.category ? (getLabel(condition.category) ?? null) : null;

  const conditionText = (
    <Box
      sx={{
        textOverflow: "ellipsis",
        overflow: "hidden",
        display: "block",
        maxWidth: "100%",
        alignItems: "center",
        "& > *": { display: "inline", paddingRight: 0.5 },
      }}
    >
      <Typography sx={{ whiteSpace: "nowrap" }} variant="descriptionTinyBold">
        {categoryLabel}
      </Typography>
      <Typography sx={{}}>•</Typography>
      <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
        {subjectLabel}
      </Typography>
      {condition.operator !== null && !subjectMissing && (
        <Typography
          fontWeight={600}
          sx={(theme) => ({
            color: theme.palette.grey[700],
            whiteSpace: "nowrap",
          })}
        >
          {getOperatorLabel(condition.operator)}
        </Typography>
      )}
      <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
        {valString}
      </Typography>
    </Box>
  );
  return (
    <OverflowTooltip slotProps={{ tooltip: { sx: { "& *": { color: "white !important" } } } }} title={conditionText}>
      {conditionText}
    </OverflowTooltip>
  );
};
